import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { Search, Users, Mail, Phone, MapPin, Hash, Calendar, X } from "lucide-react";

const ROLE_COLORS = {
  superadmin: { bg:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"rgba(245,158,11,0.3)" },
  admin:      { bg:"rgba(59,130,246,0.15)", color:"#3b82f6", border:"rgba(59,130,246,0.3)" },
  member:     { bg:"rgba(34,197,94,0.15)",  color:"#22c55e", border:"rgba(34,197,94,0.3)"  },
  volunteer:  { bg:"rgba(168,85,247,0.15)", color:"#a855f7", border:"rgba(168,85,247,0.3)" },
  intern:     { bg:"rgba(236,72,153,0.15)", color:"#ec4899", border:"rgba(236,72,153,0.3)" },
};

const ROLE_FILTERS = ["all", "superadmin", "admin", "member", "volunteer", "intern"];

export default function MemberDirectory() {
  const { currentUser } = useAuth();
  const [members,    setMembers]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [search,     setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, "users"), orderBy("name")));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMembers(data);
        setFiltered(data);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  useEffect(() => {
    let res = members;
    if (roleFilter !== "all") res = res.filter(m => m.role === roleFilter);
    if (search) res = res.filter(m =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.memberId?.toLowerCase().includes(search.toLowerCase()) ||
      m.university?.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(res);
  }, [search, roleFilter, members]);

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Member Directory</h2>
          <p style={s.sub}>{members.length} members in Help Them Shine Trust</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div style={s.filterBar}>
        <div style={{ position:"relative", flex:1, minWidth:200 }}>
          <Search size={15} color="#52504d" style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)" }}/>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, ID or university..."
            style={s.searchInput}
          />
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {ROLE_FILTERS.map(r => {
            const rc = ROLE_COLORS[r];
            return (
              <button key={r} onClick={() => setRoleFilter(r)} style={{
                padding:"7px 14px", borderRadius:8, border:"1px solid",
                borderColor: roleFilter===r ? (rc?.color||"#f59e0b") : "rgba(255,255,255,0.08)",
                background:  roleFilter===r ? (rc?.bg||"rgba(245,158,11,0.15)") : "transparent",
                color:       roleFilter===r ? (rc?.color||"#f59e0b") : "#78716c",
                fontSize:11, fontWeight:700, cursor:"pointer",
                fontFamily:"inherit", textTransform:"capitalize",
              }}>
                {r === "all" ? `All (${members.length})` : `${r} (${members.filter(m=>m.role===r).length})`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      <div style={{ fontSize:12, color:"#52504d", marginBottom:16 }}>
        Showing {filtered.length} of {members.length} members
      </div>

      {/* Grid */}
      {loading ? (
        <div style={s.empty}>Loading members...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No members found</div>
      ) : (
        <div style={s.grid}>
          {filtered.map(m => (
            <MemberCard
              key={m.id}
              member={m}
              isMe={m.id === currentUser?.uid}
              onClick={() => setSelected(m)}
            />
          ))}
        </div>
      )}

      {selected && (
        <ProfileModal
          member={selected}
          isMe={selected.id === currentUser?.uid}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function MemberCard({ member, isMe, onClick }) {
  const rc = ROLE_COLORS[member.role] || ROLE_COLORS.member;
  return (
    <div onClick={onClick} style={s.card} className="member-card">
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
        <div style={{
          width:52, height:52, borderRadius:"50%",
          background:rc.bg, border:`2px solid ${rc.border}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:20, fontWeight:800, color:rc.color, flexShrink:0,
        }}>
          {(member.name||"?").charAt(0).toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#fef3e2", marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {member.name || "—"}
            {isMe && <span style={{ fontSize:10, background:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.3)", padding:"1px 6px", borderRadius:50, marginLeft:6, fontWeight:700 }}>You</span>}
          </div>
          <div style={{ display:"inline-flex", alignItems:"center", fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:50, background:rc.bg, color:rc.color, border:`1px solid ${rc.border}`, textTransform:"uppercase", letterSpacing:"0.5px" }}>
            {member.role || "member"}
          </div>
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        <InfoRow icon={Hash}      value={member.memberId}   placeholder="No ID assigned"/>
        <InfoRow icon={Calendar}  value={member.joinDate}   placeholder="Join date not set"/>
        <InfoRow icon={MapPin}    value={member.address}    placeholder="Location not set"/>
        <InfoRow icon={Users}     value={member.university} placeholder="University not set"/>
      </div>

      <div style={{ marginTop:14, padding:"8px 0 0", borderTop:"1px solid rgba(255,255,255,0.05)", fontSize:11, color:"#f59e0b", fontWeight:700, textAlign:"center" }}>
        View Full Profile →
      </div>
    </div>
  );
}

function InfoRow({ icon:Icon, value, placeholder }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <Icon size={12} color="#52504d" style={{ flexShrink:0 }}/>
      <span style={{ fontSize:12, color: value ? "#a8a29e" : "#3a3835", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
        {value || placeholder}
      </span>
    </div>
  );
}

function ProfileModal({ member, isMe, onClose }) {
  const rc = ROLE_COLORS[member.role] || ROLE_COLORS.member;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={s.closeBtn}><X size={18}/></button>

       {member.photoUrl ? (
  <img src={member.photoUrl} alt={member.name}
    style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",border:`2px solid ${rc.border}`,flexShrink:0}}/>
) : (
  <div style={{width:52,height:52,borderRadius:"50%",background:rc.bg,border:`2px solid ${rc.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:rc.color,flexShrink:0}}>
    {(member.name||"?").charAt(0).toUpperCase()}
  </div>
)}
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{
            width:80, height:80, borderRadius:"50%",
            background:rc.bg, border:`3px solid ${rc.border}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:32, fontWeight:800, color:rc.color,
            margin:"0 auto 14px",
          }}>
            {(member.name||"?").charAt(0).toUpperCase()}
          </div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:"#fef3e2", margin:"0 0 8px" }}>
            {member.name || "No Name"}
            {isMe && <span style={{ fontSize:11, background:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.3)", padding:"2px 8px", borderRadius:50, marginLeft:8 }}>You</span>}
          </h2>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:11, fontWeight:800, padding:"4px 12px", borderRadius:50, background:rc.bg, color:rc.color, border:`1px solid ${rc.border}`, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6 }}>
            {member.role || "member"}
          </div>
          <div style={{ fontSize:12, color:"#52504d", marginTop:4 }}>
            {member.memberId || "No ID"}
          </div>
        </div>

        {/* Info Grid */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          {[
            { icon:Mail,     label:"Email",       value:member.email },
            { icon:Phone,    label:"Phone",       value:member.phone },
            { icon:MapPin,   label:"Address",     value:member.address },
            { icon:Calendar, label:"Joined",      value:member.joinDate },
            { icon:Users,    label:"University",  value:member.university },
            { icon:Hash,     label:"Member ID",   value:member.memberId },
          ].map(({ icon:Icon, label, value }) => (
            <div key={label} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"12px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
                <Icon size={12} color="#f59e0b"/>
                <span style={{ fontSize:10, fontWeight:700, color:"#52504d", textTransform:"uppercase", letterSpacing:"0.5px" }}>{label}</span>
              </div>
              <div style={{ fontSize:13, fontWeight:600, color: value ? "#fef3e2" : "#3a3835" }}>
                {value || "Not set"}
              </div>
            </div>
          ))}
        </div>

        {/* Status */}
        <div style={{ display:"flex", justifyContent:"center" }}>
          <span style={{
            fontSize:12, fontWeight:700, padding:"5px 16px", borderRadius:50,
            background: member.status==="active" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
            color: member.status==="active" ? "#22c55e" : "#ef4444",
            border: `1px solid ${member.status==="active" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
          }}>
            ● {member.status || "active"}
          </span>
        </div>
      </div>
    </div>
  );
}

const s = {
  header: { marginBottom:20 },
  title: { fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub: { fontSize:13, color:"#78716c", margin:0 },
  filterBar: { display:"flex", gap:10, flexWrap:"wrap", background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:14, marginBottom:12 },
  searchInput: { width:"100%", padding:"9px 12px 9px 36px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  grid: { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14 },
  card: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18, cursor:"pointer", transition:"all 0.2s" },
  empty: { fontSize:13, color:"#52504d", textAlign:"center", padding:"48px 0" },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(8px)" },
  modal: { background:"#111009", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:28, width:"100%", maxWidth:520, position:"relative", maxHeight:"90vh", overflowY:"auto" },
  closeBtn: { position:"absolute", top:16, right:16, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%", width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#a8a29e" },
};
